import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import * as BizCharts from 'bizcharts';
import useMountEffect from '../../hooks/useMountEffect';
import '../charts/chartTheme';
import { withNiceScale } from '../charts/chartUtils';
import {
	COMPARISON_SERIES_CURRENT,
	COMPARISON_SERIES_FIELD,
	COMPARISON_SERIES_PREVIOUS,
} from '../../utils/AnalyticsComparisonUtils';
import { ANALYTICS_CHART_TOOLTIP_ITEM_TPL } from './analyticsChartTooltipTemplates';
import {
	CATEGORY_BREAKDOWN_BAR_HEIGHT_PX,
	CATEGORY_COMPARISON_CATEGORY_PADDING_INNER,
	CATEGORY_COMPARISON_DODGE_MARGIN_RATIO,
	CATEGORY_COMPARISON_DODGE_RATIO,
} from './categoryBreakdownChartConstants';

const { Chart, Coordinate, Axis, Geom, Legend, Tooltip } = BizCharts;
const AXIS_LINE_COLOR = '#D9D9D9';
const VALUE_AXIS_TITLE_OFFSET_PX = 40;

/** Labels sit in the left chart margin, right-aligned to the y-axis line. */
const CATEGORY_AXIS_LABEL_OFFSET_PX = 8;

/**
 * Long category names (e.g. vendor display names like "Stacey's Super Speedy Service Company")
 * overflow the 200px left padding and get clipped. Truncate them at this length so they fit
 * the band cleanly; the full name is exposed via a native `<title>` decoration so hovering
 * the label still shows the full string.
 */
const CATEGORY_LABEL_MAX_CHARS = 25;

const truncateCategoryLabel = (raw: any): string => {
	if (raw === null || raw === undefined) return '';
	const text = String(raw);
	if (text.length <= CATEGORY_LABEL_MAX_CHARS) return text;
	return text.slice(0, CATEGORY_LABEL_MAX_CHARS - 1).trimEnd() + '…';
};

const CATEGORY_AXIS_LABEL_STYLE = {
	fontSize: 12,
	fontFamily: 'Roboto',
	fill: 'rgba(0,0,0,0.45)',
	textAlign: 'right',
	textBaseline: 'middle',
} as const;

const CROSSHAIRS_CONFIG = {
	type: 'x' as const,
	line: {
		style: {
			stroke: 'rgba(85, 85, 85, 0.8)',
			lineWidth: 1,
			lineDash: [4, 4] as [number, number],
		},
	},
};

// G2 applies its tooltip theme `domStyles` INLINE, which beats our scoped `.g2-tooltip` CSS
// overrides. Its defaults give the container an asymmetric, loose `10px 10px 6px 10px` padding
// (plus title/list-item margins) that made the single (no-comparison) tooltip look bottom-heavy.
// Override them here so the tooltip gets tight, balanced padding in line with the comparison and
// lifecycle tooltips. (Comparison mode uses `customContent` with its own styled container.)
const SINGLE_TOOLTIP_DOM_STYLES: Record<string, React.CSSProperties> = {
	'g2-tooltip': {
		padding: '8px 12px',
		borderRadius: '6px',
		boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
		backgroundColor: 'rgba(255,255,255,0.96)',
	},
	'g2-tooltip-title': { margin: '0', display: 'none' },
	'g2-tooltip-list': { margin: '0', padding: '0' },
	'g2-tooltip-list-item': { margin: '0', padding: '0' },
};

const CATEGORY_AXIS_LINE = {
	style: { stroke: AXIS_LINE_COLOR },
};

export type CategoryBreakdownBarChartProps = {
	data: any[];
	categoryField?: string;
	valueField?: string;
	hasComparison: boolean;
	/** Explicit y-axis category order for comparison mode (matches single-mode `primaryChartRows` order). */
	categoryOrder?: string[];
	currentColor: string;
	previousColor: string;
	height: number;
	chartPadding?: [number, number, number, number];
	cols?: any;
	formatValueLabel: (val: any) => string;
	valueAxisTitle?: string;
	renderComparisonTooltip?: (title: string, items: any[]) => string;
	singleTooltipItemTemplate?: string;
};

const CategoryBreakdownBarChart = ({
	data,
	categoryField = 'fieldName',
	valueField = 'subTotal',
	hasComparison,
	categoryOrder,
	currentColor,
	previousColor,
	height,
	chartPadding,
	cols,
	formatValueLabel,
	valueAxisTitle,
	renderComparisonTooltip,
	singleTooltipItemTemplate,
}: CategoryBreakdownBarChartProps) => {
	const seriesColor = (seriesValue: any) =>
		seriesValue === COMPARISON_SERIES_PREVIOUS ? previousColor : currentColor;

	const wrapperRef = useRef<HTMLDivElement | null>(null);
	const [containerWidth, setContainerWidth] = useState<number | null>(null);

	// We measure the container width ourselves and size the chart EXPLICITLY (autoFit is OFF below),
	// instead of letting BizCharts `autoFit` do it. With autoFit, BizCharts v4 measures its own
	// content-grown inner wrapper rather than our fixed-height container, which races the real
	// layout and leaves comparison-mode category labels misaligned with their bars until a manual
	// window/devtools resize forces a re-measure. Owning the width removes that race: width comes
	// from here, height from the deterministic `height` prop — so the canvas size is fully known
	// before the first paint and never drifts. BizCharts re-runs changeSize when the width/height
	// props change, so resizing stays responsive without any imperative chart handling on our side.
	useMountEffect(() => {
		const container = wrapperRef.current;
		if (!container) return undefined;
		const measure = () => {
			const width = container.offsetWidth;
			if (width > 0) setContainerWidth(width);
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(container);
		return () => observer.disconnect();
	});

	// Build a quick lookup of truncated → full category name so the post-render effect can
	// stamp a native SVG `<title>` for each truncated label. Browsers natively render <title>
	// as a hover tooltip, so the full vendor / problem-type name remains accessible.
	const truncatedToFullCategory = useMemo(() => {
		const lookup = new Map<string, string>();
		(data || []).forEach((row: any) => {
			const raw = row && row[categoryField];
			if (raw === null || raw === undefined) return;
			const fullText = String(raw);
			const truncated = truncateCategoryLabel(fullText);
			if (truncated !== fullText) lookup.set(truncated, fullText);
		});
		return lookup;
	}, [data, categoryField]);

	useEffect(() => {
		const container = wrapperRef.current;
		if (!container || truncatedToFullCategory.size === 0) return;
		// G2 renders axis labels as <text> elements inside the chart's <svg>; querying by tag is
		// resilient to internal G2 class-name changes between BizCharts versions.
		const textNodes = container.querySelectorAll('text');
		textNodes.forEach((textNode) => {
			const display = textNode.textContent || '';
			const fullText = truncatedToFullCategory.get(display);
			if (!fullText) return;
			const existing = textNode.querySelector('title');
			if (existing) {
				existing.textContent = fullText;
			} else {
				const titleEl = document.createElementNS('http://www.w3.org/2000/svg', 'title');
				titleEl.textContent = fullText;
				textNode.appendChild(titleEl);
			}
		});
	});

	const scale = useMemo(() => {
		const baseScale: any = withNiceScale({
			...(cols || {}),
			[valueField]: {
				type: 'linear',
				...(cols && cols[valueField] ? cols[valueField] : {}),
				min: 0,
				nice: true,
			},
		});

		if (hasComparison) {
			baseScale[COMPARISON_SERIES_FIELD] = {
				type: 'cat',
				// Previous below current within each category row (matches SpendBarChart / Figma).
				values: [COMPARISON_SERIES_PREVIOUS, COMPARISON_SERIES_CURRENT],
			};
			baseScale[categoryField] = {
				type: 'cat',
				...(categoryOrder && categoryOrder.length > 0 ? { values: categoryOrder } : {}),
				paddingOuter: 0,
				paddingInner: CATEGORY_COMPARISON_CATEGORY_PADDING_INNER,
			};
		} else {
			// Single mode: let BizCharts auto-size bands so the bar geom auto-fits.
			// Matches the production BarChart pattern which used no explicit padding.
			baseScale[categoryField] = {
				type: 'cat',
			};
		}

		return baseScale;
	}, [cols, valueField, categoryField, hasComparison, categoryOrder]);

	const comparisonAdjust = useMemo(
		() =>
			[
				{
					type: 'dodge',
					dodgeBy: COMPARISON_SERIES_FIELD,
					marginRatio: CATEGORY_COMPARISON_DODGE_MARGIN_RATIO,
					dodgeRatio: CATEGORY_COMPARISON_DODGE_RATIO,
				},
			] as any,
		[]
	);

	const position = `${categoryField}*${valueField}`;

	const g2ChartScopeClass = hasComparison
		? 'analytics-g2-chart--comparison'
		: 'analytics-g2-chart--single';

	const singleTooltipItemTpl = singleTooltipItemTemplate || ANALYTICS_CHART_TOOLTIP_ITEM_TPL;

	const singleIntervalTooltip = useMemo(
		(): [string, (category: string, value: number) => { name: string; value: string }] => [
			`${categoryField}*${valueField}`,
			(category: string, value: number) => ({
				name: category,
				value: formatValueLabel(value),
			}),
		],
		[categoryField, valueField, formatValueLabel]
	);

	return (
		<div
			ref={wrapperRef}
			className={clsx('analytics-g2-chart w-full min-w-0 overflow-hidden', g2ChartScopeClass)}
			// Pin both min and max to the computed height so BizCharts' `autoFit` can't stretch
			// the canvas beyond the bars + axis chrome we asked for. Without the cap, sparse
			// data (few categories) left a tall slab of empty canvas between the value axis
			// title and the comparison legend below. `overflow-hidden` is a belt-and-suspenders
			// guard against any residual canvas overflow.
			style={{ height, minHeight: height, maxHeight: height }}
		>
			{containerWidth ? (
			<Chart
				// Force-remount when toggling between single and comparison modes. The two modes
				// differ in scale config (extra cat dimension for comparison), dodge adjust, geom
				// sizing, legend visibility and padding — and BizCharts caches a lot of that
				// internally on the Chart instance. Without a fresh mount, toggling NO COMPARISON
				// → VS PREV → NO COMPARISON leaves the dodge sizing applied (bars get thick) and
				// hides the bottom legend. The key flips the React identity so BizCharts builds a
				// new chart from scratch each time, guaranteeing the props take effect.
				key={hasComparison ? 'breakdown-cmp' : 'breakdown-single'}
				width={containerWidth}
				height={height}
				padding={hasComparison ? chartPadding || 'auto' : 'auto'}
				data={data}
				scale={scale}
				// autoFit OFF on purpose — we drive width/height explicitly (see the width-measuring
				// effect above). autoFit raced our fixed-height container and left comparison-mode
				// labels misaligned with their bars until a manual resize.
				autoFit={false}
				theme="openwrench"
			>
				<Coordinate transpose={true} />
				<Axis
					name={categoryField}
					line={hasComparison ? CATEGORY_AXIS_LINE : null}
					tickLine={null}
					label={
						hasComparison
							? {
									formatter: truncateCategoryLabel,
									style: CATEGORY_AXIS_LABEL_STYLE,
									offset: CATEGORY_AXIS_LABEL_OFFSET_PX,
							  }
							: {
									formatter: truncateCategoryLabel,
									style: {
										fontSize: 12,
										fontFamily: 'Roboto',
										fill: 'rgba(0,0,0,0.45)',
									},
							  }
					}
				/>
				<Axis
					name={valueField}
					title={
						valueAxisTitle
							? {
									text: valueAxisTitle,
									style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
									offset: VALUE_AXIS_TITLE_OFFSET_PX,
							  }
							: null
					}
					label={{
						formatter: formatValueLabel,
						style: {
							fontSize: 12,
							fontFamily: 'Roboto',
							fill: 'rgba(0,0,0,0.45)',
						},
					}}
					grid={{
						line: {
							style: {
								stroke: AXIS_LINE_COLOR,
								lineDash: [4, 4],
							},
						},
					}}
				/>
				{hasComparison ? (
					<Legend visible={false} />
				) : (
					<Legend
						position="bottom"
						itemName={{
							style: {
								fontSize: 12,
								fontFamily: 'Roboto',
								fill: 'rgba(0,0,0,0.65)',
							},
						}}
						marker={{ symbol: 'square' }}
					/>
				)}
				{hasComparison ? (
					<Geom
						type="interval"
						size={CATEGORY_BREAKDOWN_BAR_HEIGHT_PX}
						position={position}
						color={[COMPARISON_SERIES_FIELD, seriesColor]}
						adjust={comparisonAdjust}
					/>
				) : (
					<Geom
						type="interval"
						position={position}
						color={categoryField}
						tooltip={singleIntervalTooltip}
					/>
				)}
				{hasComparison ? (
					<Tooltip
						showTitle={false}
						shared={true}
						follow={true}
						enterable={true}
						showMarkers={false}
						showCrosshairs={true}
						crosshairs={CROSSHAIRS_CONFIG}
						customContent={renderComparisonTooltip}
					/>
				) : (
					<Tooltip
						showTitle={false}
						shared={false}
						follow={true}
						showMarkers={false}
						showCrosshairs={true}
						crosshairs={CROSSHAIRS_CONFIG}
						itemTpl={singleTooltipItemTpl}
						domStyles={SINGLE_TOOLTIP_DOM_STYLES}
					/>
				)}
			</Chart>
			) : null}
		</div>
	);
};

export default CategoryBreakdownBarChart;
